import ChannelService from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelService';
import {
  AlertFormSteps,
  selectActionsData,
  selectNameAlertData,
  selectSensorsData,
  selectThresholdData,
  setStepErrorList,
} from '../../../redux/slices/alertFormSlice/alertFormSlice';
import { selectThresholdRangeById } from '../../../redux/slices/thresholdRangeSlice/thresholdRangeSlice';
import { selectTagList } from '../../../redux/slices/tagListSlice/tagListSlice';
import { selectQuietTimes } from '../../../redux/slices/quietTimesSlice/quietTimesSlice';
import {
  selectNotificationTemplates,
} from '../../../redux/slices/notificationTemplatesSlice/notificationTemplatesSlice';
import { selectNotificationGroupList } from '../../../redux/slices/notificationGroupsSlice/notificationGroupsSlice';

export const validateSelectSensorsSection = () => (dispatch, getState) => {
  const state = getState();
  const { selectedChannels } = selectSensorsData(state);
  const errorList = [];

  if (!selectedChannels || selectedChannels.length === 0) {
    errorList.push({
      field: 'sensorList',
      message: 'No sensors selected',
    });
  } else {
    const firstChannelObj = ChannelService.getChannelFromChannelKey(
      selectedChannels[0],
    );
    const initialReadingType = firstChannelObj.readingType.id;

    const allMatch = selectedChannels.every((channel) => {
      const channelObj = ChannelService.getChannelFromChannelKey(channel);
      return channelObj.readingType.id === initialReadingType;
    });

    if (!allMatch) {
      errorList.push({
        field: 'sensorList',
        message: 'All selected sensors must be of the same type',
      });
    }
  }

  dispatch(setStepErrorList({ step: AlertFormSteps.SELECT_SENSORS, errorList }));
  return errorList.length === 0;
};

export const validateDefineThresholdSection = () => (dispatch, getState) => {
  const state = getState();
  const {
    thresholdValue,
    thresholdOperator,
    thresholdId,
    useSharedThreshold,
  } = selectThresholdData(state);
  const { selectedChannels } = selectSensorsData(state);
  const errorList = [];

  if (useSharedThreshold === true) {
    if (thresholdId <= 0) {
      errorList.push(
        { field: 'thresholdId', message: 'A valid threshold must be selected' },
      );
    }
    const channelReadingType = ChannelService.getChannelFromChannelKey(
      selectedChannels[0],
    ).readingType.id;
    const threshold = selectThresholdRangeById(state, thresholdId);
    if (!threshold) {
      errorList.push(
        {
          field: 'thresholdId',
          message: 'A valid threshold must be selected',
        },
      );
    } else if (threshold.readingType !== channelReadingType) {
      errorList.push(
        {
          field: 'thresholdId',
          message: `The selected threshold is not compatible with the selected sensor${selectedChannels.length > 1 ? 's' : ''}`,
        },
      );
    }
  } else {
    if (thresholdValue === null || thresholdValue === '') {
      errorList.push({
        field: 'thresholdValue',
        message: 'Threshold value is required',
      });
    }

    if (!thresholdOperator) {
      errorList.push({
        field: 'thresholdOperator',
        message: 'Threshold operator is required',
      });
    }
  }
  dispatch(
    setStepErrorList({ step: AlertFormSteps.DEFINE_THRESHOLD, errorList }),
  );
  return errorList.length === 0;
};

export const validateDefineActionSection = () => (dispatch, getState) => {
  const state = getState();
  const { actions } = selectActionsData(state);
  const quietTimes = selectQuietTimes(state) || [];
  const templates = selectNotificationTemplates(state) || [];
  const notificationGroups = selectNotificationGroupList(state) || [];
  const errorList = [];

  if (actions && actions.length > 0) {
    actions.forEach((action, index) => {
      if (!action.notificationGroupIdList || action.notificationGroupIdList.length === 0) {
        errorList.push({
          field: `actions[${index}].notificationGroupIdList`,
          message: 'At least one notification group is required',
        });
      } else {
        const allGroupsValid = action.notificationGroupIdList
          .every((groupId) => notificationGroups.some((group) => group.id === groupId));
        if (!allGroupsValid) {
          errorList.push({
            field: `actions[${index}].notificationGroupIdList`,
            message: 'One or more selected notification groups are invalid',
          });
        }

        if (action.delay !== null && action.delay < 0) {
          errorList.push({
            field: `actions[${index}].delay`,
            message: 'Delay must be at least 0',
          });
        }

        if (action.repeatEnabled) {
          if (action.repeatInterval === null || action.repeatInterval < 0) {
            errorList.push({
              field: `actions[${index}].repeatInterval`,
              message: 'Repeat interval required when repeat is enabled',
            });
          }
        }

        if (action.maxRepeatEnabled
          && (action.maxReminders === null || action.maxReminders < 0)) {
          errorList.push({
            field: `actions[${index}].maxReminders`,
            message: 'Max number of repeats required when repeat is enabled',
          });
        }

        if (action.quietTimeId && !quietTimes.some((qt) => qt.id === action.quietTimeId)) {
          errorList.push({
            field: `actions[${index}].quietTimeId`,
            message: 'Selected Quiet Time is invalid',
          });
        }

        if (action.templateId && !templates.some((t) => t.id === action.templateId)) {
          errorList.push({
            field: `actions[${index}].templateId`,
            message: 'Selected Template is invalid',
          });
        }
      }
    });
  }

  dispatch(setStepErrorList({ step: AlertFormSteps.DEFINE_ACTIONS, errorList }));
  return errorList.length === 0;
};

export const validateNameAlertAndMoreSection = () => (dispatch, getState) => {
  const state = getState();
  const nameAlertFormData = selectNameAlertData(state);
  const selectedSensorsData = selectSensorsData(state);
  const tagList = selectTagList(state);
  const isMultipleSensors = selectedSensorsData.selectedChannels.length > 1;
  const errorList = [];

  if (!nameAlertFormData.alertNameInput
    || nameAlertFormData.alertNameInput.trim() === '') {
    errorList.push({
      field: 'alertNameInput',
      message: 'Alert name is required',
    });
  }

  if (!nameAlertFormData.alertTags) {
    errorList.push({
      field: 'alertTags',
      message: 'Tag list is in invalid state',
    });
  } else {
    nameAlertFormData.alertTags.forEach((tag) => {
      const tagExists = tagList.some((knownTag) => knownTag.id === tag.id);
      if (!tagExists) {
        errorList.push({
          field: 'alertTags',
          message: `Unknown tag: ${tag.name}`,
        });
      }
    });
  }

  if (!nameAlertFormData.timeZoneName
    || nameAlertFormData.timeZoneName.trim() === '') {
    errorList.push({
      field: 'timeZoneName',
      message: 'Time zone name is required',
    });
  }

  if (isMultipleSensors) {
    if (nameAlertFormData.additionalSensors.length !== 0) {
      errorList.push({
        field: 'additionalSensors',
        message: 'Additional sensors are not supported for multiple sensors',
      });
    }
  } else {
    nameAlertFormData.additionalSensors.forEach((channelKey) => {
      const channel = ChannelService.getChannelFromChannelKey(channelKey);
      if (channel.index === 999) {
        errorList.push({
          field: 'additionalSensors',
          message: `Invalid sensor data for key: ${channelKey}`,
        });
      }
    });
  }

  dispatch(setStepErrorList({ step: AlertFormSteps.NAME_ALERT, errorList }));
  return errorList.length === 0;
};
